/**
 * Port Tracking Utilities
 * 
 * Track usage of power and USB ports across the device tree
 */

import { Device } from '../types';

export interface PortUsage {
  deviceId: string;
  deviceName: string;
  outputIndex: number;
  portIndex: number;
  used: boolean;
  connectedDeviceId?: string;
  connectedDeviceName?: string;
}

export interface DevicePortSummary {
  deviceId: string;
  deviceName: string;
  powerPorts: PortUsage[];
  usbPorts: PortUsage[];
  powerPortsUsed: number;
  powerPortsTotal: number;
  usbPortsUsed: number;
  usbPortsTotal: number;
}

/**
 * Get all power port usage for a device
 */
export function getPowerPortUsage(device: Device, allDevices: Device[]): PortUsage[] {
  const ports: PortUsage[] = [];
  
  if (!device.powerOutputs) return ports;

  device.powerOutputs.forEach((output, outputIndex) => {
    for (let portIndex = 0; portIndex < output.portCount; portIndex++) {
      // Find if any device is connected to this specific port
      const connectedDevice = allDevices.find(d => 
        d.powerConnection?.sourceDeviceId === device.id &&
        d.powerConnection?.outputIndex === outputIndex &&
        d.powerConnection?.portIndex === portIndex
      );

      ports.push({
        deviceId: device.id,
        deviceName: device.name,
        outputIndex,
        portIndex,
        used: !!connectedDevice,
        connectedDeviceId: connectedDevice?.id,
        connectedDeviceName: connectedDevice?.name,
      });
    }
  });

  return ports;
}

/**
 * Get all USB port usage for a device
 */
export function getUsbPortUsage(device: Device, allDevices: Device[]): PortUsage[] {
  const ports: PortUsage[] = [];
  
  if (!device.usbOutputs) return ports;

  device.usbOutputs.forEach((output, outputIndex) => {
    for (let portIndex = 0; portIndex < output.count; portIndex++) {
      // Find if any device is connected to this specific port
      const connectedDevice = allDevices.find(d => 
        d.usbConnection?.sourceDeviceId === device.id &&
        d.usbConnection?.outputIndex === outputIndex &&
        d.usbConnection?.portIndex === portIndex
      );

      ports.push({
        deviceId: device.id,
        deviceName: device.name,
        outputIndex,
        portIndex,
        used: !!connectedDevice,
        connectedDeviceId: connectedDevice?.id,
        connectedDeviceName: connectedDevice?.name,
      });
    }
  });

  return ports;
}

/**
 * Get complete port summary for a device
 */
export function getDevicePortSummary(device: Device, allDevices: Device[]): DevicePortSummary {
  const powerPorts = getPowerPortUsage(device, allDevices);
  const usbPorts = getUsbPortUsage(device, allDevices);

  return {
    deviceId: device.id,
    deviceName: device.name,
    powerPorts,
    usbPorts,
    powerPortsUsed: powerPorts.filter(p => p.used).length,
    powerPortsTotal: powerPorts.length,
    usbPortsUsed: usbPorts.filter(p => p.used).length,
    usbPortsTotal: usbPorts.length,
  };
}

/**
 * Get port summaries for all devices that have outputs
 */
export function getAllPortSummaries(devices: Device[]): DevicePortSummary[] {
  return devices
    .filter(d => (d.powerOutputs && d.powerOutputs.length > 0) || (d.usbOutputs && d.usbOutputs.length > 0))
    .map(d => getDevicePortSummary(d, devices));
}

/**
 * Check if a specific power port is available
 */
export function isPowerPortAvailable(
  sourceDevice: Device,
  outputIndex: number,
  allDevices: Device[],
  excludeDeviceId?: string
): boolean {
  const connectedDevice = allDevices.find(d => 
    d.id !== excludeDeviceId &&
    d.powerConnection?.sourceDeviceId === sourceDevice.id &&
    d.powerConnection?.outputIndex === outputIndex
  );
  
  return !connectedDevice;
}

/**
 * Check if a specific USB port is available
 */
export function isUsbPortAvailable(
  sourceDevice: Device,
  outputIndex: number,
  allDevices: Device[],
  excludeDeviceId?: string
): boolean {
  const connectedDevice = allDevices.find(d => 
    d.id !== excludeDeviceId &&
    d.usbConnection?.sourceDeviceId === sourceDevice.id &&
    d.usbConnection?.outputIndex === outputIndex
  );
  
  return !connectedDevice;
}
